// product/RelatedProducts.jsx
// Horizontal scroll of other products from the same seller.
// Fetches from /api/marketplace/products?seller=<store_slug>&per_page=8
// Hides itself if no related products are found.

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { brand } from "../shared/brand";

const API_URL = process.env.REACT_APP_API_URL;

export function RelatedProducts({ storeSlug, currentProductId }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!storeSlug) return;
    setLoading(true);
    fetch(`${API_URL}/api/marketplace/products?seller_slug=${storeSlug}&per_page=8`)
      .then(r => r.json())
      .then(data => {
        const all = data.products || [];
        setProducts(all.filter(p => p.id !== currentProductId));
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [storeSlug, currentProductId]);

  if (loading) {
    return (
      <div>
        <div className="h-4 w-40 rounded bg-slate-100 animate-pulse mb-4" />
        <div className="flex gap-3 overflow-hidden">
          {[1,2,3,4].map(i => (
            <div key={i} className="shrink-0 w-36 rounded-2xl overflow-hidden border border-slate-100">
              <div className="aspect-square bg-slate-100 animate-pulse" />
              <div className="p-2 space-y-1.5">
                <div className="h-2.5 w-3/4 rounded bg-slate-100 animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-slate-200 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <div>
      <h2 className="text-sm font-extrabold text-slate-700 uppercase tracking-wide mb-4">
        Más de esta tienda
      </h2>
      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
        {products.map(p => {
          const to = `/${storeSlug}/${p.product_slug || p.sku}`;
          return (
            <Link
              key={p.id}
              to={to}
              className="shrink-0 w-36 rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <div className="aspect-square bg-slate-100 overflow-hidden">
                {p.primary_image ? (
                  <img
                    src={p.primary_image}
                    alt={p.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Package className="size-8 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs text-slate-700 font-semibold leading-tight line-clamp-2 mb-1">
                  {p.title}
                </p>
                <p className="text-xs font-extrabold" style={{ color: brand.primary }}>
                  S/ {parseFloat(p.price).toFixed(2)}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}